import { createAuth0Client } from '@auth0/auth0-spa-js'
import { registerAuthProvider, assertProviderConfig } from './auth'
import type { AuthClient, AuthProviderContext } from './types'

/** Auth0 auth provider factory — pass to createFoundation({ auth: auth0Auth }) or import to auto-register */
export const auth0Auth = async (config: Record<string, unknown>, ctx: AuthProviderContext): Promise<AuthClient> => {
  const auth0 = assertProviderConfig(config, 'auth0', ['domain', 'clientId']) as
    { domain: string; clientId: string; audience?: string; scope?: string }

  const client = await createAuth0Client({
    domain: auth0.domain,
    clientId: auth0.clientId,
    authorizationParams: {
      audience: auth0.audience,
      scope: auth0.scope || 'openid profile email',
      redirect_uri: window.location.origin
    },
    useRefreshTokens: true,
    cacheLocation: 'localstorage'
  })

  const domain = auth0.domain
  const clientId = auth0.clientId

  return {
    login: (options) => client.loginWithRedirect(options),
    logout: (options) => client.logout({ logoutParams: { returnTo: window.location.origin }, ...options }),
    getUser: async () => {
      const user = await client.getUser()
      if (!user) return undefined
      return { id: user.sub || '', email: user.email || '', name: user.name, picture: user.picture }
    },
    getTokenSilently: (options) => client.getTokenSilently(options),
    isAuthenticated: () => client.isAuthenticated(),

    async handleCallback(url?: string) {
      await client.handleRedirectCallback(url)
    },

    async confirmSignUp() {
      // Auth0 confirms via email link, not code — nothing to do on the client
      // The user clicks the verification link in their email
    },

    async resendSignUpCode() {
      // Auth0 verification resend routes through the backend (requires management API access)
      const token = await client.getTokenSilently().catch(() => null)
      if (!token) throw new Error('Must be authenticated to resend verification email')
      const response = await fetch(`${ctx.accountBaseUrl}/api/v1/accounts/account/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Foundation-Mvp-Application-Id': ctx.appId,
          'X-Foundation-Mvp-Tenant-Id': ctx.tenantId,
          'X-Foundation-Mvp-Application-Version': ctx.version
        }
      })
      if (!response.ok) {
        throw new Error('Failed to resend verification email')
      }
    },

    async signIn(email: string, password: string) {
      const response = await fetch(`https://${domain}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grant_type: 'password',
          client_id: clientId,
          username: email,
          password,
          audience: auth0.audience,
          scope: auth0.scope || 'openid profile email'
        })
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.error_description || err.message || 'Sign in failed')
      }
      // Auth0 doesn't have multi-step sign-in. Email verification is tracked via
      // user.email_verified and handled by the backend, not as a blocking step.
      return { isSignedIn: true, nextStep: { signInStep: 'DONE' } }
    },

    async signUp(email: string, password: string, metadata?: Record<string, unknown>) {
      const response = await fetch(`https://${domain}/dbconnections/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          email,
          password,
          connection: 'Username-Password-Authentication',
          ...metadata
        })
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.description || err.message || 'Sign up failed')
      }
      const data = await response.json().catch(() => ({}))
      // Auth0 signup completes immediately. Verification is via email link, handled out-of-band.
      return {
        isSignUpComplete: true,
        userId: data._id || data.user_id,
        nextStep: { signUpStep: 'DONE' }
      }
    },

    async forgotPassword(email: string) {
      const response = await fetch(`https://${domain}/dbconnections/change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          email,
          connection: 'Username-Password-Authentication'
        })
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.error_description || err.message || 'Password reset request failed')
      }
    },

    async resetPassword() {
      throw new Error('Auth0 password reset is completed via the email link, not a code')
    }
  }
}

// Auto-register when imported
registerAuthProvider('auth0', auth0Auth)
